/**
 * The shared body of every reference picker: a `command` list in a popover, keyboard-first, with
 * an optional clear. Platform-free — the touch variants are group 10's.
 */
import { useState, type ReactNode } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export interface PickerItem {
  value: string;
  /** The displayed label, e.g. `DAL01 — Dallas RDC`. */
  label: string;
  /**
   * Trailing marks for the option — a state chip, a `BadgeCheck` for the current plan. Display
   * only: the search still matches `label`.
   */
  adornment?: ReactNode;
}

export interface CommandPickerProps {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  allowClear?: boolean;
  items: PickerItem[];
  loading?: boolean;
  placeholder: string;
  emptyText?: string;
  /** Controlled search, for a server-searched picker. Omit to filter the items locally. */
  search?: string;
  onSearchChange?: (next: string) => void;
  trigger?: ReactNode;
}

export function CommandPicker({
  value,
  onChange,
  allowClear = true,
  items,
  loading = false,
  placeholder,
  emptyText = "No match",
  search,
  onSearchChange,
  trigger,
}: CommandPickerProps) {
  const [open, setOpen] = useState(false);
  const current = items.find((item) => item.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            className="type-body h-[var(--control-height-md)] w-full justify-between gap-1"
          >
            <span className={current ? "type-key" : "text-muted-foreground"}>
              {current?.label ?? placeholder}
            </span>
            <ChevronsUpDown aria-hidden className="size-3.5 opacity-60" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command shouldFilter={onSearchChange === undefined}>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={onSearchChange}
            className="type-body"
          />
          <CommandList>
            {loading ? (
              <div className="type-caption text-muted-foreground p-sm">Loading…</div>
            ) : (
              <CommandEmpty className="type-caption text-muted-foreground p-sm">
                {emptyText}
              </CommandEmpty>
            )}
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.label}
                  onSelect={() => {
                    onChange(item.value === value && allowClear ? null : item.value);
                    setOpen(false);
                  }}
                  className="type-body"
                >
                  <Check
                    aria-hidden
                    className={`mr-1 size-3.5 ${item.value === value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="flex min-w-0 flex-1 items-center gap-1">
                    <span className="truncate">{item.label}</span>
                    {item.adornment}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {allowClear && value ? (
            <div className="border-border border-t p-2xs">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="type-caption w-full justify-start gap-1"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <X aria-hidden className="size-3" /> Clear
              </Button>
            </div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
